import { ReelSpinMechanic, TumbleMechanic, TumbleReelGridPresenter, buildTumbleReelsReplayPlugin } from "@slot/mechanics-reels";

import type { SlotCannonScene } from "../scene/createScene";
import { GRID_AREA_SIZE } from "../scene/GridContainer";
import { WIN_PRESENTATION_TUNING, SYMBOL_WIN_ROTATE_SYMBOL_IDS } from "../tuning/winPresentationTuning";
import { WIN_METER_TIPS_TUNING } from "../tuning/winMeterTipsTuning";
import { FS_LAND_SHINE_TUNING, SPINE_LAND_SKIP_SYMBOL_IDS, SPRITE_LAND_SYMBOL_IDS, SQUASH_LAND_TUNING } from "../tuning/symbolLandTuning";
import { resolveSymbolWinFrames } from "../tuning/symbolWinFrames";
import { BIG_WIN_TUNING } from "../tuning/bigWinTuning";
import { FEATURE_TRIGGER_TUNING } from "../tuning/featureTriggerTuning";
import { FEATURE_FINISHED_TUNING } from "../tuning/featureFinishedTuning";
import { REEL_MOTION_TUNING } from "../tuning/reelMotionTuning";
import { REEL_TUMBLE_TUNING } from "../tuning/reelTumbleTuning";
import { LAND_BOUNCE_TUNING } from "../tuning/landBounceTuning";
import { SCATTER_LIGHT_TUNING } from "../tuning/scatterLightTuning";
import { ScatterDropLight } from "../presentation/ScatterDropLight";
import { createCaptainPlugin } from "../presentation/CaptainPlugin";
import { ANTICIPATION_BACKGROUND_TUNING } from "../tuning/anticipationBackgroundTuning";
import {
  SYMBOL_SPINE_LAND_CLIP,
  SYMBOL_SPINE_LAND_ENABLED,
  SYMBOL_SPINE_IDLE_CLIP,
  SYMBOL_SPINE_IDLE_ENABLED,
  SYMBOL_SPINE_WIN_CLIP,
  SYMBOL_SPINE_WIN_ENABLED,
  SYMBOL_SPINE_OUT_CLIP,
  SYMBOL_SPINE_OUT_ENABLED,
  SYMBOL_SPINE_CLIP_MIX,
} from "../assets/symbols/symbolSpineCatalog";
import { REEL_FILLER_SYMBOL_IDS } from "../assets/symbols/symbolCatalog";
import { WinMeterPresenter } from "../views/win-meter/WinMeterPresenter";
import { cannonFeature, cannonMultiplierTarget } from "../features/cannon/CannonFeature";
import { chanceFeature } from "../features/chance/ChanceFeature";
import { ChanceRemovalEffect } from "../features/chance/ChanceRemovalEffect";
import { CardFlipGamblePresenter } from "../views/gamble/CardFlipGamblePresenter";
import { BrightnessSymbolLandEffect, CompositeSymbolLandEffect, CompositeSymbolWinEffect, DelimitedGridCodec, DropThud, GridLayer, OverlayFadeSymbolWinEffect, SquashSymbolLandEffect, LandBounce, PitchedLandSound, RotateSymbolWinEffect, ScalePulseSymbolWinEffect, ScatterHighlightEffect, ShineSweepSymbolWinEffect, SymbolLandSounds, WinHighlightEffect, createSymbolSpineEffects } from "@slot/presentation";
import type { SymbolWinEffect } from "@slot/presentation";
import { CompositeGridPresenter } from "@slot/mechanics-core";
import { BootGridPresenter, WinSequencePresenter, createGridAudio, createShellRoundPlugins } from "@slot/game-shell";
import type { GamePresentation, PresentationDeps } from "@slot/game-shell";

/** Symbol cell size in design units (the prefab's `GridRoot` cell). */
const GRID_CELL_SIZE = 120;

/**
 * This game's round presentation (plan §2.2): the grid and its mechanics, the symbol effects, the
 * layers and presenters, the shell's win sequence and the plugin list — everything the shell builds
 * the engine, the runtime and the control bar around. Since decision 46 the shared behaviour is the
 * engine's (the Spine symbol effects, the win sequence, the shell's half of the round in its order);
 * this file names this game's pieces.
 *
 * Mechanic: mechanics-reels TUMBLE variant (GridLayer + ReelSpinMechanic + TumbleMechanic +
 * TumbleReelGridPresenter + buildTumbleReelsReplayPlugin) — cluster/mask wins, remove → gravity drop-in
 * refill. A win is highlighted ONCE (dim + Spine win + amount label) then the winning symbols tumble
 * away. Game features listed as plugins: the cannon (win multiplier with queue), the chance
 * detonation; plus the modal panels (big win, feature trigger / finished, transition) and the
 * card-flip gamble. The captain behind the grid cheers the wins and dances the big wins (his plugin
 * leads the list).
 */
export function createPresentation(scene: SlotCannonScene, deps: PresentationDeps): GamePresentation {
  const { game, gameModals } = scene;
  const s = deps.services;
  const log = s.log;
  const runner = s.presentationTaskRunner;

  // ── ONE Spine skeleton per cell and the effects through it (the engine's wiring, decision 46): the win and chance
  // dim cascades over the cell's content node, since a Spine covers the sprite ────────────────────────────────────
  const spine = createSymbolSpineEffects({
    sources: game.symbolSpineSources,
    runner,
    driver: deps.spineDriver,
    clips: {
      idle: SYMBOL_SPINE_IDLE_ENABLED ? SYMBOL_SPINE_IDLE_CLIP : null,
      land: SYMBOL_SPINE_LAND_ENABLED ? SYMBOL_SPINE_LAND_CLIP : null,
      win: SYMBOL_SPINE_WIN_ENABLED ? SYMBOL_SPINE_WIN_CLIP : null,
      out: SYMBOL_SPINE_OUT_ENABLED ? SYMBOL_SPINE_OUT_CLIP : null,
    },
    landSkipSymbolIds: SPINE_LAND_SKIP_SYMBOL_IDS,
    ambientClips: [SYMBOL_SPINE_IDLE_CLIP],
    clipMix: SYMBOL_SPINE_CLIP_MIX,
  });
  const symbolSpineHost = spine.host;
  const alphaDim = spine.dim;

  const anticipationBg = game.anticipationBackground;
  anticipationBg.setTuning(ANTICIPATION_BACKGROUND_TUNING);

  const gridAudio = createGridAudio({ audioService: s.audioService, runner });

  // ── The grid and its mechanics (plan step 5): the layer owns cells, pool and layout; the reel
  // and tumble mechanics move symbols on it; the symbol effects are this game's ─────────────────
  const gridLayer = new GridLayer(
    {
      gridRoot: game.gridContainer.gridRoot,
      layoutRoot: game.gridContainer.gridArea,
      layoutArea: GRID_AREA_SIZE,
      gridColumnCount: deps.grid.columns,
      gridRowCount: deps.grid.rows,
      cellWidth: GRID_CELL_SIZE,
      cellHeight: GRID_CELL_SIZE,
      autoFitToLayoutRoot: true,
      centerGrid: true,
      // Five evenly spaced reels (the layout's default offsets are the 6-column padded set).
      columnOffsets: [0, 0, 0, 0, 0],
    },
    {
      definitionResolver: game.symbolCatalog,
      log,
      // Landing reactions, in play order: the thud, the pitched scatter stinger, the other symbol
      // sounds, the FS / CH grid bounce, then the FS light at the cannon's mouth (this game's rules;
      // the shell only dispatches).
      landReactions: [
        new DropThud({ audio: gridAudio }),
        new PitchedLandSound({ audio: gridAudio, soundId: "grid_scatter_drop", nowMs: () => runner.engineSeconds * 1000 }),
        new SymbolLandSounds({ audio: gridAudio, ownedElsewhere: new Set(["grid_scatter_drop"]) }),
        new LandBounce({ target: game.gridContainer.gridArea, runner, triggerSymbolIds: new Set(["FS", "CH"]), tuning: LAND_BOUNCE_TUNING }),
        ...(game.scatterLight ? [new ScatterDropLight({ light: game.scatterLight, runner, triggerSymbolIds: new Set(["FS"]), tuning: SCATTER_LIGHT_TUNING })] : []),
      ],
      // The Spine effects' bind and release hooks: the sprite shown, the cell's dim released, the cell (un)hosted.
      onSymbolBound: spine.onSymbolBound,
      onSymbolReleased: spine.onSymbolReleased,
    },
  );

  // The land animation plays on the SPIN reveal only, and only for the symbols outside the skip set
  // (`SPINE_LAND_SKIP_SYMBOL_IDS`: the card ranks, and for now H2 / H3 / FS). The tumble mechanic is wired without it.
  // Without Spine symbols the scatter's sprite look plays instead: the squash pop with the brightness shine.
  const spriteLandSymbolIds = new Set(SPRITE_LAND_SYMBOL_IDS);
  const landEffect =
    spine.landEffect ??
    new CompositeSymbolLandEffect([
      new SquashSymbolLandEffect({ targetSymbolIds: spriteLandSymbolIds, tuning: SQUASH_LAND_TUNING, taskRunner: runner }),
      new BrightnessSymbolLandEffect({ targetSymbolIds: spriteLandSymbolIds, tuning: FS_LAND_SHINE_TUNING, taskRunner: runner }),
    ]);

  const reel = new ReelSpinMechanic(gridLayer, {
    taskRunner: runner,
    audio: gridAudio,
    motionTuning: REEL_MOTION_TUNING,
    // Filler-symbol motion blur via pre-blurred atlas frames (the drum's filler scrolls blurred).
    fillerBlurDefinitions: game.symbolCatalog.fillerBlurDefinitions().size > 0 ? game.symbolCatalog.fillerBlurDefinitions() : null,
    fillerSymbolIds: REEL_FILLER_SYMBOL_IDS,
    speedModeProvider: () => s.presentationClock.getMode(),
    anticipationFrame: game.createAnticipationFrame ? { create: game.createAnticipationFrame, parent: game.gridContainer.gridOverlay } : null,
    anticipationSessionListener: {
      onStart: (ordinal) => {
        anticipationBg.noteReelStart(ordinal);
        if (ordinal === 1) {
          s.audioService.control.duckMusic(ANTICIPATION_BACKGROUND_TUNING.musicDuckScale);
        }
      },
      onEnd: () => {
        anticipationBg.rampOut();
        s.audioService.control.unduckMusic();
      },
    },
    landEffect,
  });

  // A removed cell plays its Spine `out` (CH and FS carry none: they shrink with the spritesheet burst), except the
  // detonating chance barrel: its `win` is its explosion, so `ChanceRemovalEffect` lets its cell leave with that clip.
  const removalEffect = new ChanceRemovalEffect(spine.removalEffect);
  const tumble = new TumbleMechanic(gridLayer, {
    taskRunner: runner,
    // No `landEffect` (user, 2026-09-11): a symbol arriving by TUMBLING lands plain. The mechanic
    // keeps the capability for a title that wants its chain drops to animate.
    // Removal-burst VFX: the spritesheet explosion played on each removed cell during a tumble.
    removalEffectSpawner: game.removalEffectSpawner,
    removalEffect,
  });

  // ── Round presentation, in construction order (plan step 6, pass 1): presenters and effects →
  // `WinSequencePresenter` → the plugin list; the shell builds the engine and the runtime around it.
  // Nothing here is built inside a callback.
  const gridCodec = new DelimitedGridCodec(deps.grid.columns, deps.grid.rows);

  const basePresenter = new TumbleReelGridPresenter({
    reel,
    tumble,
    gridCodec,
    presentationBarrier: s.presentationBarrier,
    tumbleTuning: REEL_TUMBLE_TUNING,
    log,
  });

  // Winning-symbol animation: every winner plays its own Spine `win` clip (idle resumes after).
  // The sprite composite (pulse + rotate + shine-sweep) stays as the kill-switch fallback.
  const symbolWinEffect: SymbolWinEffect =
    spine.winEffect ??
    (() => {
      const lSymbolIds = new Set(SYMBOL_WIN_ROTATE_SYMBOL_IDS);
      const scalePulseEffect = new ScalePulseSymbolWinEffect(
        (symbolId) => (lSymbolIds.has(symbolId) ? WIN_PRESENTATION_TUNING.scalePulseL : WIN_PRESENTATION_TUNING.scalePulse),
        runner,
        { once: true },
      );
      const shineSweepEffect = new ShineSweepSymbolWinEffect({ tuning: WIN_PRESENTATION_TUNING.shineSweep, taskRunner: runner, once: true });
      const rotateEffect = new RotateSymbolWinEffect(WIN_PRESENTATION_TUNING.rotate, runner, { symbolIds: lSymbolIds, once: true });
      // The H symbols switch to their alt frame and KEEP it until they tumble away, when the atlas carries one.
      const symbolWinFrames = resolveSymbolWinFrames(scene.symbolsAtlas);
      const frameSwitch =
        symbolWinFrames.size > 0
          ? [
              new OverlayFadeSymbolWinEffect({
                framesBySymbol: symbolWinFrames,
                taskRunner: runner,
                fadeInSeconds: WIN_PRESENTATION_TUNING.symbolFrames.fadeInSeconds,
                startDelayMilliseconds: WIN_PRESENTATION_TUNING.symbolFrames.startDelayMilliseconds,
                baseFadeOutDelaySeconds: WIN_PRESENTATION_TUNING.symbolFrames.baseFadeOutDelaySeconds,
                hold: true,
                markDirtyOnHold: true,
              }),
            ]
          : [];
      return new CompositeSymbolWinEffect([
        // FIRST: suspend the winning cell's ambient idle overlay — these effects are sprite-based.
        { play: (request) => symbolSpineHost?.suspend(gridLayer.getSymbolViewAtIndex(request.index)), stopAll: () => {} },
        ...frameSwitch,
        scalePulseEffect,
        rotateEffect,
        shineSweepEffect,
      ]);
    })();

  const winHighlightEffect = new WinHighlightEffect({
    gridView: gridLayer,
    log,
    moneyFormatter: s.moneyContext.formatter,
    createWinAmountLabel: game.createWinAmountLabel,
    overlayRoot: game.fxOverlays,
    timings: WIN_PRESENTATION_TUNING.layer,
    labelTuning: WIN_PRESENTATION_TUNING.label,
    symbolWinEffect,
    exposureOwner: alphaDim,
    // The tumble's win label flies into the win meter's amount (WIN_PRESENTATION_TUNING.layer.labelCollect).
    labelCollectTarget: game.winMeterView.collectTarget,
    taskRunner: runner,
  });

  const scatterHighlightEffect = new ScatterHighlightEffect({ gridView: gridLayer, taskRunner: runner, log });

  const gridPresenter = new CompositeGridPresenter({ base: basePresenter, effects: [winHighlightEffect] });

  /** Per-SPIN teardown + scroll start — runs before EVERY reel spin (click + every spin boundary). */
  const beginReelSpinCycle = (): void => {
    gridPresenter.clearForNewSpin();
    reel.startContinuousSpin();
  };

  const bootGridPresenter = new BootGridPresenter({
    grid: { reset: () => reel.resetPresentationState(), paint: (encoded) => basePresenter.paintGrid(encoded) },
    waitForNextRenderedFrame: deps.waitForNextRenderedFrame,
    log,
  });

  const mechanic = buildTumbleReelsReplayPlugin({
    gridPresenter,
    basePresenter,
    runner,
    postRemovalHoldSeconds: REEL_TUMBLE_TUNING.removal.postRemovalHoldSeconds,
  });

  // The plank shows the spin's total, or tips while the game is idle (none in replay mode: a replay never idles the coordinator).
  const winMeterPresenter = game.winMeterView
    ? new WinMeterPresenter({
        panel: game.winMeterView,
        moneyContext: s.moneyContext,
        runner,
        chromeRunner: s.chromeTaskRunner,
        spinPhase: s.spinPhaseStore,
        tips: deps.replayMode ? { ...WIN_METER_TIPS_TUNING, keys: [] } : WIN_METER_TIPS_TUNING,
      })
    : null;

  // The game features: each builds its presenter from what it names. The cannon's shot lands on the win the shell's
  // win sequence holds at its base; the chance detonation is its own action and its plugin drives its presenter.
  const playFeatureSound = (soundId: string): void => {
    s.audioService.presentation.playSoundId?.(soundId);
  };
  const cannonPresenter = cannonFeature.createPresenter({ view: game.cannonView, createBullet: game.createCannonBullet, runner, playSound: playFeatureSound, log });
  const chancePresenter = chanceFeature.createPresenter({
    grid: gridLayer,
    cellSize: () => {
      const layout = gridLayer.getComputedLayout();
      return { width: layout.cellWidth, height: layout.cellHeight };
    },
    runner,
    symbolSpineHost,
    markers: game.chanceMarkers,
    removalEffect,
    // The removal a win's `remove_symbols_by_mask` runs (`buildTumbleReelsReplayPlugin`), with the same hold.
    removeCells: async (positionMask) => {
      await gridPresenter.removeSymbolsByMask(positionMask);
      await runner.sleep(REEL_TUMBLE_TUNING.removal.postRemovalHoldSeconds);
    },
    dimPort: alphaDim,
    playSound: playFeatureSound,
    log,
  });

  // The win sequence (the shell's): the beat before each win, the cue and haptic, the per-win slam; a multiplied win's
  // label waits at its base for the cannon's impact; the tumble's label flies into this game's win meter.
  const winSequencePresenter = new WinSequencePresenter({
    runner,
    audioService: s.audioService,
    hapticsService: s.hapticsService,
    winHighlightEffect,
    winMeter: winMeterPresenter,
    tuning: WIN_PRESENTATION_TUNING,
    deferMultiplierMerge: true,
  });

  // ── The replay plugins: the shell's half of the round in its one order (its overlay presenters over this game's
  // panels), with the mechanic and this game's features in their slots (decision 46) ───────────────────────────────
  const plugins = createShellRoundPlugins({
    services: s,
    lifecycle: deps.lifecycle,
    panels: {
      featureTrigger: gameModals.featureTriggerView,
      featureFinished: gameModals.featureFinishedView,
      bigWin: gameModals.bigWinView,
      gameTransition: gameModals.gameTransitionView,
    },
    tuning: { featureTrigger: FEATURE_TRIGGER_TUNING, featureFinished: FEATURE_FINISHED_TUNING, bigWin: BIG_WIN_TUNING, autoAdvance: WIN_PRESENTATION_TUNING.autoAdvance },
    winSequence: winSequencePresenter,
    scatterHighlightEffect,
    beginSpinCycle: beginReelSpinCycle,
    // Each spin's cannon shot plays its fire-sound ladder from the start.
    onSpinStart: () => cannonPresenter?.resetShotSequence(),
    mechanic: mechanic.plugin,
    features: [
      cannonFeature.createPlugin(cannonMultiplierTarget(cannonPresenter, () => winSequencePresenter.mergeActiveWinLabels())),
      chanceFeature.createPlugin(chancePresenter),
    ],
  });

  // The gamble view's presenter; the shell's createRuntime builds the `GambleSession` around it.
  const gamblePresentation = gameModals.gambleView
    ? new CardFlipGamblePresenter({
        panel: gameModals.gambleView,
        formatter: s.moneyContext.formatter,
        localization: s.localization,
        runner: s.chromeTaskRunner,
        log,
      })
    : null;

  return {
    framePresenter: mechanic.framePresenter,
    // The captain's reactions first: each starts with its action, before the shell presents it.
    plugins: [createCaptainPlugin(game.captainView), ...plugins],
    bootGridPresenter,
    // Spin-on-click (pre-response): reload the cannon's base ladder, run the per-spin
    // teardown + start the reel scroll, then release the "spin" barrier.
    preSpin: async () => {
      cannonPresenter?.loadBaseLadder();
      beginReelSpinCycle();
      s.presentationBarrier.release("spin");
    },
    // Manual slam: the reels brake all columns together under their own "reel_motion" scope; the
    // same tap snaps the FIRST-PASS win line currently presenting, or lands the win flying into the meter.
    slam: () => {
      const reelSlammed = reel.requestSlamStop();
      const winSnapped = winSequencePresenter.slamActiveWin();
      const collectSnapped = winSequencePresenter.slamWinCollect();
      return reelSlammed || winSnapped || collectSnapped;
    },
    slamScopes: mechanic.slamScopes,
    gamblePresentation,
  };
}
